// Bootstrap: wires config -> Game SDK -> DiceController -> PixiJS DiceScene.
// The ENTIRE game (background, dice, selector, controls) is rendered by the
// scene; the DOM holds only auxiliary panels (loading, history, rules,
// session-expired) and an accessible control mirror. The controller is the
// single source of truth and the only thing that talks to the backend.

// MUST be the FIRST import: under our strict CSP (`script-src 'self'`, no
// 'unsafe-eval'), PixiJS cannot use `new Function`/`eval` to generate its
// shader/uniform sync code, so `Application.init()` throws. This side-effect
// import installs Pixi's static (no-eval) polyfills and must run before ANY
// renderer is initialized. It does NOT enable unsafe-eval — it removes the need.
import 'pixi.js/unsafe-eval';
import { GameClient, ParentBridge } from '@aerofruits/game-sdk';
import { prefersReducedMotion, readViewport, ViewportManager } from '@aerofruits/game-shell';
import type { SupportedLocale } from '@aerofruits/i18n';
import { buildReplayUrl, DEFAULT_BET_MINOR, GAME_ID, resolveConfig } from './config.js';
import { DiceController, type HistoryEntry, type ViewModel } from './game/controller.js';
import { formatMultiplierTenths } from './game/lightning-types.js';
import { planReplay } from './game/replay.js';
import { getI18n, initI18n, switchLocale } from './i18n/setup.js';
import { DiceScene } from './scene/scene.js';
import { loadPreferences, savePreferences } from './state/preferences.js';
import { applyStaticDom, applySoundDom, formatMoneyForLocale } from './ui/dom-locale.js';
import { getSoundManager } from './audio/sound-manager.js';
import { mountLanguageSelector } from './ui/language-selector.js';
import { LightningPanel } from './ui/lightning-panel.js';
import { LightningImpact } from './ui/lightning-impact.js';
import type { RoundLightningSnapshot } from './ui/lightning-panel.js';
import { ResultBlock } from './ui/result-block.js';
import { ErrorToast } from './ui/error-toast.js';
import { OrientationOverlay } from './ui/orientation-overlay.js';
import { isOrientationBlocked } from './ui/orientation-policy.js';
import type { Viewport } from '@aerofruits/game-shell';

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el;
}

async function main(): Promise<void> {
  const cfg = resolveConfig();
  const prefs = loadPreferences();

  const i18n = await initI18n({
    sessionLocale: cfg.locale ?? null,
    queryLocale: new URLSearchParams(window.location.search).get('lang'),
    savedLocale: prefs.locale,
    browserLocale: typeof navigator !== 'undefined' ? navigator.language : null,
  });

  const formatMoney = (minor: number, currency: string): string =>
    formatMoneyForLocale(i18n, minor, currency);

  const loadingText = $('loading-text');
  const loadingBar = $('loading-bar');
  const setProgress = (pct: number, text: string): void => {
    loadingBar.style.width = `${pct}%`;
    loadingText.textContent = text;
  };

  const reducedMotion = (): boolean => prefersReducedMotion();

  const panelHost = $('lightning-panel-host');
  const resultHost = $('lightning-result-host');
  const errorToastHost = $('error-toast-host');
  const gameRoot = $('game-root');
  const resultBlock = new ResultBlock(resultHost, { i18n, formatMoney });
  const errorToast = new ErrorToast(errorToastHost);
  const lightningPanel = new LightningPanel(panelHost, { i18n, strikeHost: gameRoot });
  const lightningImpact = new LightningImpact($('lightning-impact-host'));
  const orientationOverlay = new OrientationOverlay($('orientation-overlay'), i18n);

  const client = new GameClient({ gatewayBaseUrl: cfg.gatewayBaseUrl });
  const controller = new DiceController(
    {
      exchangeLaunchToken: (t) => client.exchangeLaunchToken(t),
      getBalance: () => client.getBalance(),
      roll: (p) => client.rollLightningNumberDice(p),
      recoverAfterReload: () => client.recoverAfterReload(),
      closeSession: () => client.closeSession(),
    },
    {
      betOptions: cfg.betOptions,
      initialBetMinor: prefs.betMinor,
      defaultBetMinor: DEFAULT_BET_MINOR,
      initialSelectedNumber: prefs.selectedNumber,
      lightning: lightningPanel,
      resultBlock,
      i18n,
      formatMoney,
      reducedMotion,
    },
  );

  const persistBet = (betMinor: number): void => {
    prefs.betMinor = betMinor;
    savePreferences(prefs);
  };
  const selectNumber = (n: number): void => {
    if (controller.selectNumber(n)) {
      prefs.selectedNumber = n;
      savePreferences(prefs);
    }
  };

  const openHistory = (): void => $('history').removeAttribute('hidden');
  const openRules = (): void => $('rules').removeAttribute('hidden');
  const toggleSound = (): void => {
    void getSoundManager().unlock();
    prefs.muted = !prefs.muted;
    savePreferences(prefs);
    scene.setSoundOn(!prefs.muted);
    applySoundDom(prefs.muted, i18n);
  };

  // Replay is a read-only re-presentation of a completed round; while it runs we
  // suppress every live gameplay action so a stored round can never become a new
  // real round or mutate the active session/wallet.
  let replaying = false;
  let orientationBlocked = false;
  lightningPanel.setOnSelect((n) => {
    if (!replaying && !orientationBlocked) {
      void getSoundManager().unlock();
      getSoundManager().play('ui.select');
      selectNumber(n);
    }
  });
  const liveOnly =
    (fn: () => void): (() => void) =>
    () => {
      if (!replaying && !orientationBlocked) fn();
    };

  let replayLightningSnapshot: RoundLightningSnapshot | null = null;

  const startReplay = async (entry: HistoryEntry): Promise<void> => {
    if (replaying || controller.state() !== 'READY') return;
    const plan = planReplay(entry);
    if (!plan) return;
    replaying = true;
    replayLightningSnapshot = lightningPanel.captureRoundState();
    $('history').setAttribute('hidden', '');
    showReplayBadge(plan.roundId, plan.selectedNumber);
    resultBlock.prepareRoll();
    lightningPanel.prepareRoll();
    try {
      if (plan.lightningSlots.length > 0) {
        await lightningPanel.revealLightning(plan.lightningSlots, reducedMotion());
      }
      lightningPanel.setSelectedNumber(plan.selectedNumber);
      await scene.playReplay(plan, reducedMotion());
      lightningPanel.setMatchHighlights(plan.dice, plan.selectedNumber);
      lightningPanel.commitRound(plan.lightningSlots, plan.dice, plan.selectedNumber);
      lightningPanel.enableAfterRound();
      if (plan.matchCount === 0) {
        resultBlock.showLoss();
      } else {
        resultBlock.showWin({
          matchCount: plan.matchCount,
          baseProfitMultiplierTenths: plan.baseProfitMultiplierTenths,
          lightningProfitMultiplierTenths: plan.lightningProfitMultiplierTenths,
          profitMultiplierTenths: plan.profitMultiplierTenths,
          profitAmountMinor: plan.profitAmountMinor,
          stakeReturnMultiplierTenths: plan.stakeReturnMultiplierTenths ?? 10,
          stakeReturnAmountMinor: plan.stakeReturnAmountMinor ?? plan.betMinor,
          totalPayoutMultiplierTenths: plan.totalPayoutMultiplierTenths,
          totalPayoutAmountMinor: plan.totalPayoutAmountMinor,
          currency: plan.currency,
        });
      }
    } finally {
      finishReplay();
    }
  };
  const finishReplay = (): void => {
    if (!replaying) return;
    replaying = false;
    $('replay').setAttribute('hidden', '');
    scene.endReplay(controller.viewModel());
    if (replayLightningSnapshot) {
      lightningPanel.restoreRoundState(replayLightningSnapshot);
      replayLightningSnapshot = null;
    } else {
      lightningPanel.restoreCompletedRound();
    }
  };

  const scene = new DiceScene({
    formatMoney,
    reducedMotion,
    getLightningCardRect: (n) => lightningPanel.getCardScreenRect(n),
    showLightningImpact: (tenths, rm) => lightningImpact.show(tenths, rm),
    cancelLightningImpact: () => lightningImpact.cancel(),
    translate: {
      t: (key, params) => i18n.t(key, params),
      tp: (base, count, params) => i18n.tp(base, count, params),
    },
    callbacks: {
      onRoll: liveOnly(() => void controller.roll()),
      onBetUp: liveOnly(() => persistBet(controller.nextBet())),
      onBetDown: liveOnly(() => persistBet(controller.prevBet())),
      onMaxBet: liveOnly(() => persistBet(controller.maxBet())),
      onSelectNumber: (n) => {
        if (!replaying && !orientationBlocked) selectNumber(n);
      },
      onHistory: openHistory,
      onRules: openRules,
      onSound: toggleSound,
    },
  });

  const applyOrientationPolicy = (vp: Viewport): void => {
    const blocked = isOrientationBlocked(vp);
    orientationBlocked = blocked;
    gameRoot.dataset.orientationBlocked = String(blocked);
    if (blocked) {
      orientationOverlay.show();
      scene.setInteractionBlocked(true);
      lightningPanel.cancel();
      lightningImpact.cancel();
      scene.animatorPort().cancel?.();
    } else {
      orientationOverlay.hide();
      scene.setInteractionBlocked(false);
    }
  };

  const langRoot = $('language');
  const langUi = mountLanguageSelector({
    root: langRoot,
    i18n,
    onSelect: async (locale: SupportedLocale) => {
      prefs.locale = locale;
      savePreferences(prefs);
      await switchLocale(locale);
    },
  });
  $('open-language').addEventListener('click', () => {
    void getSoundManager().unlock();
    getSoundManager().play('ui.click');
    langUi.open();
  });
  applyStaticDom(i18n);
  applySoundDom(prefs.muted, i18n);

  setProgress(20, i18n.t('dice.status.loading'));
  scene.setLayoutBoundsListener((bounds) => {
    resultBlock.setBounds(bounds.resultBlock);
    lightningPanel.setBounds(bounds.numberSelector);
    lightningImpact.setBounds(bounds.lightningImpact);
    errorToast.setBounds(bounds.errorToast);
  });
  await scene.init(gameRoot, readViewport(gameRoot));
  scene.setSoundOn(!prefs.muted);
  controller.setAnimator(scene.animatorPort());
  setProgress(60, i18n.t('dice.status.connecting'));

  let bridge: ParentBridge | undefined;
  if (cfg.allowedParentOrigins.length > 0) {
    bridge = new ParentBridge({ allowedParentOrigins: cfg.allowedParentOrigins });
    bridge.onMessage((msg) => {
      if (msg.type === 'OPERATOR_SET_MUTE') {
        prefs.muted = msg.muted;
        savePreferences(prefs);
        scene.setSoundOn(!prefs.muted);
      }
      if (msg.type === 'OPERATOR_REQUEST_CLOSE') void controller.close();
    });
  }

  i18n.subscribe(() => {
    applyStaticDom(i18n);
    applySoundDom(prefs.muted, i18n);
    langUi.refresh();
    scene.applyTranslations();
    resultBlock.applyTranslations();
    orientationOverlay.applyTranslations(i18n);
    renderDom(controller.viewModel(), bridge, (entry) => void startReplay(entry), cfg.gatewayBaseUrl, errorToast, i18n, gameRoot);
  });

  rollHook = liveOnly(() => void controller.roll());
  wireDom(selectNumber, () => replaying, () => scene.cancelReplay());

  // Wire the accessible control mirror to the same controller actions.
  $('roll').addEventListener('click', liveOnly(() => {
    void getSoundManager().unlock();
    getSoundManager().play('dice.rollPress');
    void controller.roll();
  }));
  $('bet-up').addEventListener('click', liveOnly(() => persistBet(controller.nextBet())));
  $('bet-down').addEventListener('click', liveOnly(() => persistBet(controller.prevBet())));
  $('max-bet').addEventListener('click', liveOnly(() => persistBet(controller.maxBet())));
  $('btn-history').addEventListener('click', openHistory);
  $('btn-rules').addEventListener('click', openRules);
  $('btn-sound').addEventListener('click', toggleSound);
  applySoundDom(prefs.muted, i18n);
  $('replay-close').addEventListener('click', () => scene.cancelReplay());

  controller.subscribe((vm) => {
    scene.update(vm);
    if (!replaying && vm.state === 'READY' && vm.selectedNumber === null && vm.dice === null) {
      resultBlock.showHint();
    } else if (!replaying && vm.state === 'READY' && vm.selectedNumber !== null && vm.dice === null) {
      resultBlock.hide();
    }
    renderDom(vm, bridge, (entry) => void startReplay(entry), cfg.gatewayBaseUrl, errorToast, i18n, gameRoot);
  });

  controller.beginLoading();

  const viewportManager = new ViewportManager({
    gameRoot,
    onLayout: (vp, insets) => {
      applyOrientationPolicy(vp);
      scene.relayout(vp, insets);
    },
    onVisibility: (hidden) => scene.setHidden(hidden),
  });
  viewportManager.start();

  if (!cfg.launchToken) {
    setProgress(100, i18n.t('dice.status.missingToken'));
    showBanner(i18n.t('dice.errors.missingToken'));
    return;
  }

  await controller.start(cfg.launchToken);
  // Reveal any recovered/initial round's dice instantly (no roll animation).
  scene.showResult(controller.viewModel());
  setProgress(100, i18n.t('dice.status.ready'));
  $('loading').setAttribute('hidden', '');
  bridge?.post({ type: 'GAME_READY', gameId: GAME_ID });

  document.addEventListener('visibilitychange', () => {
    const sm = getSoundManager();
    if (document.hidden) sm.onVisibilityHidden();
    else void sm.onVisibilityVisible();
  });
}

function showBanner(text: string): void {
  const b = $('banner');
  b.textContent = text;
  b.removeAttribute('hidden');
  window.setTimeout(() => b.setAttribute('hidden', ''), 4000);
}

/** Shows the "Replay" mode badge so replay is clearly distinct from live play. */
function showReplayBadge(roundId: string, pick: number): void {
  setText('replay-info', `Round ${roundId.slice(0, 8)} · pick ${pick}`);
  $('replay').removeAttribute('hidden');
}

let rollHook: (() => void) | undefined;
let lastSettledRound: string | null = null;

function wireDom(selectNumber: (n: number) => void, isReplaying: () => boolean, cancelReplay: () => void): void {
  $('history-close').addEventListener('click', () => $('history').setAttribute('hidden', ''));
  $('rules-close').addEventListener('click', () => $('rules').setAttribute('hidden', ''));

  for (let n = 1; n <= 6; n++) {
    $(`num-${n}`).addEventListener('click', () => {
      if (!isReplaying()) {
        void getSoundManager().unlock();
        getSoundManager().play('ui.select');
        selectNumber(n);
      }
    });
  }

  window.addEventListener('keydown', (e) => {
    // Escape always exits replay mode and returns safely to live play.
    if (e.key === 'Escape' && isReplaying()) {
      e.preventDefault();
      cancelReplay();
      return;
    }
    // Space/Enter rolls when focus is not on an interactive element, no drawer
    // is open, and we are not replaying. (Pixi handles in-canvas clicks.)
    if (e.key !== ' ' && e.key !== 'Enter') return;
    const tag = document.activeElement?.tagName;
    const drawerOpen =
      !$('history').hasAttribute('hidden') ||
      !$('rules').hasAttribute('hidden') ||
      !$('language').hasAttribute('hidden');
    if (tag === 'BUTTON' || drawerOpen || isReplaying()) return;
    e.preventDefault();
    rollHook?.();
  });
}

function renderDom(
  vm: ViewModel,
  bridge: ParentBridge | undefined,
  onReplay: (entry: HistoryEntry) => void,
  gatewayBaseUrl: string,
  errorToast: ErrorToast,
  i18n = getI18n(),
  gameRootEl: HTMLElement | null = null,
): void {
  const formatMoney = (minor: number, currency: string): string =>
    formatMoneyForLocale(i18n, minor, currency);
  const t = i18n.t.bind(i18n);
  const orientationBlocked = gameRootEl?.dataset.orientationBlocked === 'true';
  setText('balance', vm.balanceMinor === null ? '—' : formatMoney(vm.balanceMinor, vm.currency));
  setText('bet', formatMoney(vm.betMinor, vm.currency));
  const winShown = (vm.state === 'SHOWING_WIN' || vm.state === 'READY') && vm.lastWinMinor > 0;
  setText('win', formatMoney(winShown ? vm.lastWinMinor : 0, vm.currency));
  setText('selected', vm.selectedNumber === null ? '—' : String(vm.selectedNumber));
  setText('dice-readout', vm.dice ? `Dice: ${vm.dice.join(', ')}` : '');
  setText('game-state', vm.state);

  for (let n = 1; n <= 6; n++) {
    const el = document.getElementById(`num-${n}`);
    if (el) el.setAttribute('aria-pressed', String(n === vm.selectedNumber));
  }

  const ready = vm.state === 'READY';
  const roll = document.getElementById('roll');
  if (roll instanceof HTMLButtonElement) {
    roll.disabled = !vm.canRoll || orientationBlocked;
    roll.setAttribute('aria-disabled', String(!vm.canRoll || orientationBlocked));
  }
  setText('roll-hint', ready && vm.selectedNumber === null ? t('dice.status.selectNumber') : '');
  setDisabled('bet-up', !ready || orientationBlocked);
  setDisabled('bet-down', !ready || orientationBlocked);
  setDisabled('max-bet', !ready || orientationBlocked);
  for (let n = 1; n <= 6; n++) setDisabled(`num-${n}`, !ready || orientationBlocked);

  toggle('expired', vm.state === 'SESSION_EXPIRED');
  if (vm.message && vm.state === 'READY' && vm.errorCode && !orientationBlocked) {
    errorToast.show(vm.message);
  } else if (!vm.message) {
    errorToast.hide();
  }

  renderHistory(vm, onReplay, gatewayBaseUrl, i18n);

  if (vm.state === 'READY' && vm.lastRound && vm.lastRound.round_id !== lastSettledRound) {
    lastSettledRound = vm.lastRound.round_id;
    bridge?.post({ type: 'GAME_ROUND_SETTLED', roundId: vm.lastRound.round_id, totalWinMinor: vm.lastWinMinor });
    bridge?.post({ type: 'GAME_BALANCE', balanceMinor: vm.balanceMinor ?? 0, currency: vm.currency });
  }
  if (vm.state === 'SESSION_EXPIRED') bridge?.post({ type: 'GAME_SESSION_EXPIRED' });
}

function renderHistory(
  vm: ViewModel,
  onReplay: (entry: HistoryEntry) => void,
  gatewayBaseUrl: string,
  i18n = getI18n(),
): void {
  const t = i18n.t.bind(i18n);
  const formatMoney = (minor: number, currency: string): string =>
    formatMoneyForLocale(i18n, minor, currency);
  const body = $('history-body');
  body.replaceChildren();
  for (const h of vm.history) {
    // Each row formats with the ROUND's own currency, so a session that changed
    // currency (or mixed history) still renders every amount correctly.
    const ccy = h.currency || vm.currency;
    const tr = document.createElement('tr');
    const cells = [
      h.roundId.slice(0, 8),
      new Date(h.timestampMs).toLocaleTimeString(i18n.getLocale()),
      String(h.selectedNumber),
      h.dice.join(' '),
      formatMoney(h.betMinor, ccy),
      formatMoney(h.winMinor, ccy),
      formatMultiplierTenths(h.totalMultiplierTenths ?? h.multiplier),
    ];
    for (const c of cells) {
      const td = document.createElement('td');
      td.textContent = c;
      tr.appendChild(td);
    }
    const actionCell = document.createElement('td');
    actionCell.className = 'history__actions';

    const replayBtn = document.createElement('button');
    replayBtn.type = 'button';
    replayBtn.className = 'history__replay';
    replayBtn.textContent = t('dice.actions.replay');
    replayBtn.setAttribute('aria-label', `Replay round ${h.roundId.slice(0, 8)}, picked ${h.selectedNumber}`);
    replayBtn.addEventListener('click', () => onReplay(h));
    actionCell.appendChild(replayBtn);

    // Share/Copy are offered ONLY for rounds with a persisted, shareable public
    // replay id. Otherwise we show an honest unavailable hint rather than a fake
    // URL (e.g. real-money rounds that are private by operator policy).
    if (h.replayId && h.replayPublic !== false) {
      const url = buildReplayUrl(h.replayId, gatewayBaseUrl);

      const copyBtn = document.createElement('button');
      copyBtn.type = 'button';
      copyBtn.className = 'history__copy';
      copyBtn.textContent = t('dice.actions.copyLink');
      copyBtn.setAttribute('aria-label', `Copy replay link for round ${h.roundId.slice(0, 8)}`);
      copyBtn.addEventListener('click', () => void copyReplayLink(copyBtn, url));
      actionCell.appendChild(copyBtn);

      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        const shareBtn = document.createElement('button');
        shareBtn.type = 'button';
        shareBtn.className = 'history__share';
        shareBtn.textContent = t('dice.actions.share');
        shareBtn.setAttribute('aria-label', `Share replay link for round ${h.roundId.slice(0, 8)}`);
        shareBtn.addEventListener('click', () => void shareReplayLink(url));
        actionCell.appendChild(shareBtn);
      }
    } else {
      const na = document.createElement('span');
      na.className = 'history__unavailable';
      na.textContent = t('dice.history.linkUnavailable');
      na.title = 'A shareable replay link is not available for this round.';
      actionCell.appendChild(na);
    }
    tr.appendChild(actionCell);
    body.appendChild(tr);
  }
}

/** Copies a replay URL to the clipboard with graceful fallbacks + feedback. */
async function copyReplayLink(btn: HTMLButtonElement, url: string): Promise<void> {
  const ok = await copyToClipboard(url);
  const original = btn.textContent ?? 'Copy link';
  btn.textContent = ok ? 'Copied!' : 'Copy failed';
  window.setTimeout(() => {
    btn.textContent = original;
  }, 1500);
  if (!ok) showBanner('Could not copy the link. Long-press to copy manually.');
}

/** Invokes the native share sheet; falls back to copy when share is rejected. */
async function shareReplayLink(url: string): Promise<void> {
  try {
    await navigator.share({ title: 'Dice replay', text: 'Watch this Dice round replay', url });
  } catch {
    // User cancelled or share failed; offer copy as a fallback affordance.
    const ok = await copyToClipboard(url);
    if (ok) showBanner('Replay link copied to clipboard.');
  }
}

/** Clipboard write with an execCommand fallback for non-secure contexts. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function toggle(id: string, on: boolean): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (on) el.removeAttribute('hidden');
  else el.setAttribute('hidden', '');
}

function setText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

function setDisabled(id: string, disabled: boolean): void {
  const el = document.getElementById(id);
  if (el instanceof HTMLButtonElement) el.disabled = disabled;
}

main().catch((err: unknown) => {
  console.error('fatal', err);
  const t = document.getElementById('loading-text');
  if (t) t.textContent = 'Failed to start';
});
